import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Star, X, Sparkles, MapPin, ShieldCheck, HelpCircle, Send, LogIn, LogOut,
  FlaskConical, Compass, Camera, Play, CheckCircle2, ImagePlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CATEGORY_COLORS, CATEGORY_ICONS } from "@/lib/constants";
import { useLang } from "@/lib/lang";
import { useAuth, API } from "@/lib/auth";
import { toast } from "sonner";
import ShareMenu from "@/components/ShareMenu";

function Stars({ value }) {
  return (
    <div className="flex items-center gap-0.5" data-testid="confidence-stars">
      {Array.from({ length: 10 }).map((_, i) => (
        <Star
          key={i}
          size={12}
          className={i < value ? "text-[#8b6f3d] fill-[#8b6f3d]" : "text-zinc-300"}
        />
      ))}
      <span className="ml-2 font-mono-data text-[11px] text-zinc-600">{value}/10</span>
    </div>
  );
}

export default function LocationPanel({ location, onClose }) {
  const { t, lang } = useLang();
  const { user, login, logout } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);

  // Visit
  const [visited, setVisited] = useState(false);
  const [visiting, setVisiting] = useState(false);

  // Media
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setAnalysis(location?.ai_analysis?.[lang] || null);
    setNewComment("");
    if (location) {
      fetchComments();
      fetchMedia();
      fetchVisit();
      // Register view for trending counter
      axios.post(`${API}/locations/${location.id}/view`).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.id, lang]);

  const fetchComments = async () => {
    try {
      const r = await axios.get(`${API}/locations/${location.id}/comments`);
      setComments(r.data);
    } catch {}
  };

  const fetchMedia = async () => {
    try {
      const r = await axios.get(`${API}/locations/${location.id}/media`);
      setMedia(r.data);
    } catch {}
  };

  const fetchVisit = async () => {
    if (!user) return setVisited(false);
    try {
      const r = await axios.get(`${API}/visits/${location.id}`, { withCredentials: true });
      setVisited(r.data.visited);
    } catch { setVisited(false); }
  };

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const r = await axios.post(`${API}/ai/analyze`, { location_id: location.id, language: lang });
      setAnalysis(r.data.analysis);
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur d'analyse IA" : "AI analysis error");
    } finally {
      setAiLoading(false);
    }
  };

  const postComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await axios.post(
        `${API}/locations/${location.id}/comments`,
        { text: newComment.trim() },
        { withCredentials: true }
      );
      setNewComment("");
      fetchComments();
      toast.success(lang === "fr" ? "Commentaire publié" : "Comment posted");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        toast.error(lang === "fr" ? detail.message_fr : detail.message_en, { duration: 8000 });
      } else {
        toast.error(lang === "fr" ? "Erreur de publication" : "Post failed");
      }
    } finally {
      setPosting(false);
    }
  };

  const doVisit = () => {
    if (!user) { login(); return; }
    if (!navigator.geolocation) {
      toast.error(t("locationDenied"));
      return;
    }
    setVisiting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await axios.post(
            `${API}/locations/${location.id}/visit`,
            { lat: pos.coords.latitude, lng: pos.coords.longitude },
            { withCredentials: true }
          );
          if (r.data.validated) {
            setVisited(true);
            toast.success(`${t("visitSuccess")} · ${r.data.total_visits} ✨`);
          } else {
            toast.error(`${t("tooFar")} · ${Math.round(r.data.distance_m)}m`);
          }
        } catch (e) {
          toast.error(lang === "fr" ? "Erreur" : "Error");
        } finally {
          setVisiting(false);
        }
      },
      () => {
        toast.error(t("locationDenied"));
        setVisiting(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!user) { login(); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/locations/${location.id}/media`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("uploaded"));
      fetchMedia();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        toast.error(lang === "fr" ? detail.message_fr : detail.message_en, { duration: 8000 });
      } else {
        toast.error(t("uploadError"));
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!location) return null;

  const color = CATEGORY_COLORS[location.category] || "#8b6f3d";
  const name = location.name?.[lang] || location.name?.fr;
  const description = location.description?.[lang] || location.description?.fr;
  const isFiction = location.ai_generated || location.category === "ia";

  return (
    <aside
      className="fixed top-0 right-0 h-full w-full sm:w-[440px] glass border-l border-zinc-200 z-[1100] flex flex-col animate-in slide-in-from-right duration-300"
      data-testid="location-panel"
    >
      <div className="flex items-start justify-between p-5 border-b border-zinc-100">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span
              className="inline-flex items-center justify-center w-7 h-7 rounded-full text-sm border border-white shadow-sm"
              style={{ background: color, color: "#fff" }}
            >
              {CATEGORY_ICONS[location.category]}
            </span>
            <Badge variant="outline" className="border font-mono-data text-[10px] uppercase tracking-wider"
                   style={{ borderColor: color, color }}>
              {location.category}
            </Badge>
            {isFiction && (
              <Badge className="bg-pink-100 text-pink-800 border border-pink-200 hover:bg-pink-100">
                <FlaskConical size={10} className="mr-1" />{t("fictional")}
              </Badge>
            )}
            {visited && (
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100" data-testid="visited-badge">
                <CheckCircle2 size={10} className="mr-1" />{t("visited")}
              </Badge>
            )}
          </div>
          <h2 className="font-serif-mystery text-3xl leading-tight text-zinc-900" data-testid="location-name">{name}</h2>
          {location.country && (
            <div className="mt-1 flex items-center gap-1 text-zinc-500 text-sm">
              <MapPin size={12} /> {location.country}
            </div>
          )}
          <div className="mt-3">
            <ShareMenu location={location} testId="location-share-btn" />
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="close-panel-btn"
          className="p-2 rounded-full hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">{t("confidence")}</span>
            {location.confirmed ? (
              <span className="flex items-center gap-1 text-xs text-emerald-700"><ShieldCheck size={12} /> {t("confirmed")}</span>
            ) : isFiction ? (
              <span className="flex items-center gap-1 text-xs text-pink-700"><FlaskConical size={12} /> {t("fictional")}</span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-amber-700"><HelpCircle size={12} /> {t("hypothesis")}</span>
            )}
          </div>
          <Stars value={location.confidence} />
        </div>

        <div>
          <p className="text-zinc-700 leading-relaxed text-[15px]" data-testid="location-description">{description}</p>
        </div>

        {/* Adventurer visit */}
        <div className="border border-[#8b6f3d]/30 bg-amber-50/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Compass size={16} className="text-[#8b6f3d]" />
            <span className="font-mono-data text-[10px] uppercase tracking-widest text-[#8b6f3d]">{t("adventurerMode")}</span>
          </div>
          <p className="text-xs text-zinc-600 mb-3">{t("adventurerHelp")}</p>
          <Button
            onClick={doVisit}
            disabled={visiting || visited}
            data-testid="visit-here-btn"
            className="w-full bg-[#8b6f3d] hover:bg-[#a1854b] text-white disabled:opacity-60"
          >
            {visited ? (
              <><CheckCircle2 size={14} className="mr-2" /> {t("visited")}</>
            ) : (
              <><Compass size={14} className="mr-2" /> {visiting ? "…" : t("visitHere")}</>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50/50">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-1">LAT</div>
            <div className="font-mono-data text-sm text-[#8b6f3d]">{location.lat.toFixed(4)}</div>
          </div>
          <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50/50">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-1">LNG</div>
            <div className="font-mono-data text-sm text-[#8b6f3d]">{location.lng.toFixed(4)}</div>
          </div>
        </div>

        {/* AI analysis */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
              <Sparkles size={11} className="text-[#be185d]" /> {t("aiAnalysis")}
            </span>
          </div>
          {analysis ? (
            <div className="border border-pink-200 bg-pink-50/60 rounded-lg p-4">
              <div className="font-mono-data text-[13px] text-zinc-800 leading-relaxed whitespace-pre-line" data-testid="ai-analysis-text">
                {analysis}
              </div>
            </div>
          ) : (
            <Button
              onClick={generateAI}
              disabled={aiLoading}
              variant="outline"
              data-testid="generate-ai-btn"
              className="w-full border-pink-300 bg-pink-50 hover:bg-pink-100 text-pink-800"
            >
              <Sparkles size={14} className="mr-2" />
              {aiLoading ? t("generating") : t("generateAi")}
            </Button>
          )}
        </div>

        {location.sources?.length > 0 && (
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{t("sources")}</div>
            <ul className="text-xs text-zinc-600 space-y-1">
              {location.sources.map((s, i) => (<li key={i}>· {s}</li>))}
            </ul>
          </div>
        )}

        <Separator className="bg-zinc-200" />

        {/* Media gallery */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif-mystery text-xl text-[#8b6f3d] flex items-center gap-2">
              <Camera size={16} /> {t("photosVideos")}
            </h3>
            <span className="font-mono-data text-[10px] text-zinc-500">{media.length}</span>
          </div>

          {user && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/mp4,video/webm,video/quicktime"
                className="hidden"
                onChange={doUpload}
                data-testid="media-file-input"
              />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                variant="outline"
                data-testid="upload-media-btn"
                className="w-full mb-3 border-zinc-300 hover:bg-zinc-50 text-zinc-900"
              >
                <ImagePlus size={14} className="mr-2" />
                {uploading ? t("uploading") : t("uploadMedia")}
              </Button>
            </>
          )}

          {media.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-4">
              {media.map((m) => (
                <a
                  key={m.id}
                  href={`${API}/media/${m.id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`media-${m.id}`}
                  className="relative aspect-square rounded-lg overflow-hidden border border-zinc-200 group"
                >
                  {m.kind === "image" ? (
                    <img src={`${API}/media/${m.id}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  ) : (
                    <div className="w-full h-full bg-zinc-900 flex items-center justify-center">
                      <Play size={24} className="text-white" />
                    </div>
                  )}
                </a>
              ))}
            </div>
          )}
        </div>

        <Separator className="bg-zinc-200" />

        {/* Comments */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif-mystery text-xl text-[#8b6f3d]">{t("comments")}</h3>
            <span className="font-mono-data text-[10px] text-zinc-500">{comments.length}</span>
          </div>

          {user ? (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2 text-xs text-zinc-600">
                {user.picture && <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />}
                <span>{user.name}</span>
                <button onClick={logout} data-testid="logout-btn" className="ml-auto flex items-center gap-1 text-zinc-500 hover:text-zinc-900">
                  <LogOut size={12} /> {t("signOut")}
                </button>
              </div>
              <Textarea
                data-testid="comment-textarea"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={t("yourComment")}
                className="bg-zinc-50 border-zinc-200 text-zinc-900 placeholder:text-zinc-400 resize-none min-h-[80px]"
              />
              <Button
                onClick={postComment}
                disabled={posting || !newComment.trim()}
                data-testid="post-comment-btn"
                className="mt-2 bg-[#8b6f3d] hover:bg-[#a1854b] text-white"
              >
                <Send size={14} className="mr-1" />
                {t("postComment")}
              </Button>
            </div>
          ) : (
            <Button
              onClick={login}
              data-testid="login-google-btn"
              variant="outline"
              className="w-full mb-4 border-zinc-300 hover:bg-zinc-50 text-zinc-900"
            >
              <LogIn size={14} className="mr-2" />
              {t("signInGoogle")}
            </Button>
          )}

          <div className="space-y-3">
            {comments.length === 0 && (
              <p className="text-xs text-zinc-500 italic" data-testid="no-comments-text">{t("noComments")}</p>
            )}
            {comments.map((c) => (
              <div key={c.id} className="border border-zinc-200 rounded-lg p-3 bg-zinc-50/40" data-testid={`comment-${c.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <a href={`/user/${encodeURIComponent(c.user_name)}`} className="flex items-center gap-2 hover:opacity-80" data-testid={`comment-user-${c.id}`}>
                    {c.user_picture && <img src={c.user_picture} alt="" className="w-5 h-5 rounded-full" />}
                    <span className="text-xs text-zinc-800 hover:text-[#8b6f3d] hover:underline">{c.user_name}</span>
                  </a>
                  <span className="font-mono-data text-[10px] text-zinc-400 ml-auto">
                    {new Date(c.created_at).toLocaleDateString(lang)}
                  </span>
                </div>
                <p className="text-sm text-zinc-700 whitespace-pre-line">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}
